import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, of, switchMap, map, firstValueFrom } from 'rxjs';
import { TaxDocument } from '../../interfaces/tax/tax-document.interface';

@Injectable({
  providedIn: 'root'
})
export class RetentionCalculationService {
  private readonly COLLECTION_NAME = 'tax-documents';
  private readonly DEFAULT_RETENTION = 19; // España por defecto

  constructor(private afs: AngularFirestore) {}

  /**
   * Obtiene la retención aplicable para un usuario
   * Verifica si tiene certificado válido y retorna la retención correspondiente
   */
  getApplicableRetention(userId: string): Observable<number> {
    return this.hasValidTaxCertificate(userId).pipe(
      switchMap(hasValidCertificate => {
        if (hasValidCertificate) {
          // Si tiene certificado válido, obtener la retención específica de su país
          return this.getUserCountryRetention(userId);
        } else {
          // Si no tiene certificado válido, aplicar retención por defecto
          return of(this.DEFAULT_RETENTION);
        }
      })
    );
  }

  /**
   * Verifica si el usuario tiene un certificado fiscal válido y aprobado
   */
  hasValidTaxCertificate(userId: string): Observable<boolean> {
    const today = new Date();
    
    return this.afs.collection<TaxDocument>(this.COLLECTION_NAME, ref => 
      ref.where('userId', '==', userId)
         .where('status', '==', 'approved')
         .where('expiryDate', '>', today)
         .orderBy('uploadDate', 'desc')
         .limit(1)
    ).valueChanges().pipe(
      map(documents => documents.length > 0)
    );
  }

  /**
   * Obtiene la retención específica del país del usuario
   */
  private getUserCountryRetention(userId: string): Observable<number> {
    return this.afs.collection('users').doc(userId).get().pipe(
      switchMap(userDoc => {
        const userData = userDoc.data() as any;
        
        // Usar la retención almacenada en el perfil del usuario
        if (userData?.retentionPercentage !== undefined) {
          return of(userData.retentionPercentage);
        }
        
        // Como fallback, usar la retención por defecto
        return of(this.DEFAULT_RETENTION);
      })
    );
  }

  /**
   * Calcula el importe de retención para un monto bruto
   */
  calculateRetention(grossAmount: number, retentionPercentage: number): number {
    if (grossAmount <= 0 || retentionPercentage < 0) {
      return 0;
    }
    
    return (grossAmount * retentionPercentage) / 100;
  }

  /**
   * Calcula el importe neto después de aplicar la retención
   */
  getNetAmount(grossAmount: number, retentionPercentage: number): number {
    const retentionAmount = this.calculateRetention(grossAmount, retentionPercentage);
    return grossAmount - retentionAmount;
  }

  /**
   * Obtiene el porcentaje de retención para un país específico por código
   */
  getCountryRetentionPercentage(countryCode: string): number {
    // Por ahora retornar el valor por defecto, se puede mejorar consultando Firebase
    return this.DEFAULT_RETENTION;
  }

  /**
   * Obtiene el porcentaje de retención por nombre de país
   * Busca primero en Firebase 'retentions', luego usa valor por defecto
   */
  getCountryRetentionByName(countryName: string): number {
    if (!countryName || countryName.trim() === '') {
      return this.DEFAULT_RETENTION;
    }

    // Por defecto retornar 19% (España)
    // La búsqueda completa se hace en los componentes que tienen acceso async
    return this.DEFAULT_RETENTION;
  }

  /**
   * Obtiene el porcentaje de retención por nombre de país de forma asíncrona
   */
  async getCountryRetentionByNameAsync(countryName: string): Promise<number> {
    if (!countryName || countryName.trim() === '') {
      return this.DEFAULT_RETENTION;
    }

    try {
      const normalizedCountryName = countryName.trim();
      const lowerCountryName = normalizedCountryName.toLowerCase();
      
      // Buscar en la colección 'retentions' de Firebase
      const retentionsSnapshot = await firstValueFrom(this.afs.collection('retentions').get());
      
      if (retentionsSnapshot && !retentionsSnapshot.empty) {
        for (const doc of retentionsSnapshot.docs) {
          const retentionData = doc.data() as any;
          console.log(`🔍 [DEBUG] RetentionService - Buscando retención para "${normalizedCountryName}" en documento ${doc.id}`);
          console.log(`🔍 [DEBUG] RetentionService - Datos del documento:`, Object.keys(retentionData).slice(0, 10)); // Mostrar primeros 10 campos
          
          // Caso 1: Si el documento tiene countryName y percentage como campos separados
          const docCountryName = retentionData.countryName || '';
          if (docCountryName.toLowerCase() === lowerCountryName) {
            if (retentionData.percentage !== undefined && retentionData.percentage !== null) {
              console.log(`✅ Retención encontrada (estructura 1) para ${normalizedCountryName}: ${retentionData.percentage}%`);
              return retentionData.percentage;
            }
          }
          
          // Caso 2: Si el documento tiene los países como campos directamente
          // (ej: { "Alemania": 0, "España": 19, ... })
          if (retentionData && typeof retentionData === 'object') {
            // Buscar el país en los campos del documento
            for (const key in retentionData) {
              // Ignorar campos especiales de Firestore
              if (key === 'countryName' || key === 'countryCode' || key === 'percentage' || key.startsWith('_')) {
                continue;
              }
              
              // Comparar el nombre del campo con el país buscado (case-insensitive)
              const keyLower = key.toLowerCase();
              if (keyLower === lowerCountryName) {
                const percentage = retentionData[key];
                if (typeof percentage === 'number') {
                  console.log(`✅ Retención encontrada (estructura 2) para "${normalizedCountryName}" (campo: "${key}"): ${percentage}%`);
                  return percentage;
                }
              }
            }
            
            // Si no se encontró con comparación exacta, intentar búsqueda parcial
            // (por si hay diferencias de acentos o espacios)
            for (const key in retentionData) {
              if (key === 'countryName' || key === 'countryCode' || key === 'percentage' || key.startsWith('_')) {
                continue;
              }
              
              const keyLower = key.toLowerCase().trim();
              const countryLower = lowerCountryName.trim();
              
              // Comparación más flexible: eliminar acentos y espacios extra
              const normalizeString = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
              const normalizedKey = normalizeString(keyLower);
              const normalizedCountry = normalizeString(countryLower);
              
              if (normalizedKey === normalizedCountry) {
                const percentage = retentionData[key];
                if (typeof percentage === 'number') {
                  console.log(`✅ Retención encontrada (estructura 2, normalizada) para "${normalizedCountryName}" (campo: "${key}"): ${percentage}%`);
                  return percentage;
                }
              }
            }
          }
        }
      }
      
      console.warn(`⚠️ No se encontró retención para ${normalizedCountryName}, usando valor por defecto: ${this.DEFAULT_RETENTION}%`);
      return this.DEFAULT_RETENTION;
    } catch (error) {
      console.error('Error al buscar retención:', error);
      return this.DEFAULT_RETENTION;
    }
  }

  /**
   * Obtiene todos los países con sus retenciones desde Firebase
   */
  getAllCountryRetentions(): Observable<any[]> {
    return this.afs.collection('retentions').valueChanges().pipe(
      map(docs => docs.map((doc: any) => ({
        id: doc.id || '',
        countryCode: doc.countryCode || '',
        countryName: doc.countryName || '',
        percentage: doc.percentage || this.DEFAULT_RETENTION
      })))
    );
  }

  /**
   * Verifica si un país tiene retención cero
   */
  isZeroRetentionCountry(countryCode: string): boolean {
    // Por ahora retornar false, se puede mejorar consultando Firebase
    return false;
  }

  /**
   * Obtiene la retención por defecto
   */
  getDefaultRetention(): number {
    return this.DEFAULT_RETENTION;
  }

  /**
   * Calcula retención con validaciones adicionales
   */
  calculateRetentionWithValidation(
    grossAmount: number, 
    retentionPercentage: number,
    userId?: string
  ): Observable<{
    retentionAmount: number;
    netAmount: number;
    retentionPercentage: number;
    isZeroRetention: boolean;
  }> {
    return this.getApplicableRetention(userId || '').pipe(
      map(applicableRetention => {
        const finalRetentionPercentage = retentionPercentage || applicableRetention;
        const retentionAmount = this.calculateRetention(grossAmount, finalRetentionPercentage);
        const netAmount = this.getNetAmount(grossAmount, finalRetentionPercentage);
        
        return {
          retentionAmount,
          netAmount,
          retentionPercentage: finalRetentionPercentage,
          isZeroRetention: finalRetentionPercentage === 0
        };
      })
    );
  }
}
